<script setup lang="ts">
import { ref, computed, watch, onMounted, watchEffect } from "vue";

import { useI18n } from "vue-i18n";

import { toast } from "@/common/utils/toast";
import { scrollToTop } from "@/common/utils/common";

import { analysisPatch } from "@/common/api/rest/analysis/analysis";
import { evaluationCrewList } from "@/common/api/rest/evaluation/crew";

import type { AnalysisType } from "@/common/types/analysis";
import type { CrewListType } from "@/common/types/crew";

import { CardAnalysisTime } from "@/components";

import { baseSelect, baseTextarea } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update-data"]);

const props = withDefaults(
  defineProps<{
    data: AnalysisType | null;
    width?: string;
    isEdit?: boolean;
  }>(),
  {
    width: "100%",
    isEdit: true,
  },
);

const danger = ref<string | null>(null);
const assistant = ref<string | null>(null);
const technical = ref<string | null>(null);
const description = ref<string | null>(null);

const technicals = ref<{ option: string; value: string }[]>([]);
const assistants = ref<{ option: string; value: string }[]>([]);

const dangerOptions = computed(() =>
  Array.from({ length: 10 }, (_, value) => ({
    option: String(value + 1),
    value: String(value + 1),
  })),
);

function update() {
  emit("update-data");
}

function fetchCrew() {
  if (
    !props.data ||
    (assistants.value.length > 0 && technicals.value.length > 0)
  )
    return;

  evaluationCrewList(props.data.account_id, {
    evaluation_id: props.data.evaluation_id,
    limit: 100,
  })
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        assistants.value = data.list
          ?.filter((item: CrewListType) => item.type === "ASSISTANT")
          ?.map((item: CrewListType) => {
            return {
              option: item.name,
              value: item.member_id,
            };
          });

        technicals.value = data.list
          ?.filter((item: CrewListType) => item.type !== "ASSISTANT")
          ?.map((item: CrewListType) => {
            return {
              option: item.name,
              value: item.member_id,
            };
          });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function patchData(payload: Partial<AnalysisType>) {
  if (!props.data) return;

  analysisPatch(props.data.account_id, props.data.analysis_id, payload)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        update();
        fetchCrew();
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeDanger(value: number) {
  patchData({ danger: value });
}

function changeDescription() {
  patchData({ description: description.value });
}

function changeCanceledDescription(description: string) {
  patchData({ canceled_description: description });
}

function changeTechnical(value: string) {
  patchData({ member_id: value });
}

function changeAssistant(value: string) {
  patchData({ assistant_id: value });
}

watch(
  () => props.data?.evaluation_id,
  () => {
    fetchCrew();
  },
);

watchEffect(() => {
  technical.value = props.data?.member_id || null;
  assistant.value = props.data?.assistant_id || null;
  danger.value = String(props.data?.danger) || null;
  description.value = props.data?.description || null;
});

onMounted(() => {
  fetchCrew();
  scrollToTop("main");
});
</script>

<template>
  <section class="tab-component" :class="{ width: width }">
    <div class="content">
      <div class="title">
        {{ t("analysis.labels.analysis_status") }}
      </div>

      <template v-if="data">
        <card-analysis-time
          :account-id="data.account_id"
          :analysis-id="data.analysis_id"
          :status="data.status"
          :start-date="data.start_date"
          :start-edited="data.start_edited"
          :start-edited-motive="data.start_edited_motive"
          :end-date="data.end_date"
          :end-edited="data.end_edited"
          :end-edited-motive="data.end_edited_motive"
          :breaks="data.breaks"
          :canceled-description="data.canceled_description"
          :is-edit="isEdit"
          @update="update()"
          @update-description="changeCanceledDescription($event)"
        />
      </template>

      <div class="title">
        {{ t("analysis.labels.analysis_data") }}
      </div>

      <template v-if="technicals.length > 0">
        <base-select
          v-model="technical"
          :value="technical"
          :options="technicals"
          @change="changeTechnical"
        >
          <template #label> {{ t("common.labels.technical") }}</template>
        </base-select>
      </template>

      <template v-if="assistants.length > 0">
        <base-select
          v-model="assistant"
          :value="assistant"
          :options="assistants"
          @change="changeAssistant"
        >
          <template #label> {{ t("common.labels.assistant") }}</template>
        </base-select>
      </template>

      <base-select
        v-model="danger"
        :value="danger"
        :options="dangerOptions"
        @change="changeDanger"
      >
        <template #label> {{ t("analysis.labels.risk_degree") }}</template>
      </base-select>

      <base-textarea
        v-model="description"
        :value="description"
        :placeholder="t('common.placeholders.describe')"
        @blur="changeDescription()"
      >
        <template #label>
          {{ t("common.labels.observations") }}
        </template>
      </base-textarea>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.tab-component {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-block: 2rem;
  padding-inline: 1.5rem;

  .title {
    font-size: 1.15rem;
    font-weight: 700;
    text-transform: uppercase;
    padding-block-end: 0.5rem;
    border-bottom: 1px solid var(--border);
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  .box-time {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    background: var(--white-off);
    padding: 1.25rem;
    border-radius: 0.5rem;

    .actions {
      display: flex;
      flex-direction: row;
      gap: 1.25rem;
    }

    .infos {
      display: flex;
      width: 100%;
      flex-direction: column;
      align-items: flex-start;
      justify-content: flex-start;
      gap: 0.75rem;

      .line {
        display: grid;
        width: 100%;
        grid-template-columns: 8.5rem auto;
        gap: 1rem;
        font-size: 1rem;

        label {
          font-size: 1rem;
          font-weight: 500;
          line-height: 1.25rem;
          color: var(--gray-dark);
        }

        span {
          font-weight: 700;
        }
      }

      .box-time {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        align-items: center;
        justify-content: center;
        width: calc(100% - (1.25rem * 2));

        .view {
          display: flex;
          flex-direction: row;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          font-size: 2rem;
          font-weight: bold;
        }
      }
    }
  }
}
</style>
